import React, { useEffect, useMemo, useState } from "react";
import { FaTimes, FaSave } from "react-icons/fa";
import {
  DictionaryWord,
  updateDictionaryWord,
  UpdateDictionaryWordPayload,
} from "../chat/api/dictionaryApi";

interface EditWordModalProps {
  isOpen: boolean;
  word: DictionaryWord | null;
  onClose: () => void;
  onWordUpdated: (updatedWord: DictionaryWord) => void;
}

const emptyForm: UpdateDictionaryWordPayload = {
  word_base_form: "",
  word_translation: "",
  grammar: "",
  example_sentence: "",
  example_sentence_translation: "",
  user_comment: "",
};

const EditWordModal: React.FC<EditWordModalProps> = ({
  isOpen,
  word,
  onClose,
  onWordUpdated,
}) => {
  const initialFormState = useMemo(() => {
    if (!word) {
      return { ...emptyForm };
    }

    return {
      word_base_form: word.word_base_form,
      word_translation: word.word_translation,
      grammar: word.grammar,
      example_sentence: word.example_sentence,
      example_sentence_translation: word.example_sentence_translation,
      user_comment: word.user_comment ?? "",
    };
  }, [word]);

  const [formData, setFormData] = useState<UpdateDictionaryWordPayload>(
    initialFormState
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFormData(initialFormState);
  }, [initialFormState]);

  if (!isOpen || !word) {
    return null;
  }

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      const payload: UpdateDictionaryWordPayload = {
        word_base_form: formData.word_base_form?.trim(),
        word_translation: formData.word_translation?.trim(),
        grammar: formData.grammar?.trim(),
        example_sentence: formData.example_sentence?.trim(),
        example_sentence_translation:
          formData.example_sentence_translation?.trim(),
        user_comment:
          formData.user_comment !== undefined
            ? formData.user_comment?.trim() || null
            : undefined,
      };

      const updatedWord = await updateDictionaryWord(
        word.user_marked_word_id,
        payload
      );
      onWordUpdated(updatedWord);
      onClose();
    } catch (updateError: any) {
      console.error("Failed to update dictionary word:", updateError);
      setError(
        updateError?.message || "Failed to update dictionary word. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-base-100 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-base-300">
          <div>
            <h3 className="text-xl font-bold">Edit Saved Word</h3>
            <p className="text-sm text-base-content/70">
              Update the details for <strong>{word.word_base_form}</strong>
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-circle"
            aria-label="Close"
            disabled={isSaving}
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Word</span>
              </label>
              <input
                type="text"
                name="word_base_form"
                value={formData.word_base_form || ""}
                onChange={handleChange}
                className="input input-bordered"
                required
                disabled={isSaving}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Translation</span>
              </label>
              <input
                type="text"
                name="word_translation"
                value={formData.word_translation || ""}
                onChange={handleChange}
                className="input input-bordered"
                required
                disabled={isSaving}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-semibold">Grammar</span>
              </label>
              <input
                type="text"
                name="grammar"
                value={formData.grammar || ""}
                onChange={handleChange}
                className="input input-bordered"
                placeholder="e.g., Verb, Noun"
                disabled={isSaving}
              />
            </div>
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Example Sentence</span>
            </label>
            <textarea
              name="example_sentence"
              value={formData.example_sentence || ""}
              onChange={handleChange}
              className="textarea textarea-bordered"
              rows={3}
              placeholder="Add one or more example sentences..."
              disabled={isSaving}
            />
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">
                Example Translation
              </span>
            </label>
            <textarea
              name="example_sentence_translation"
              value={formData.example_sentence_translation || ""}
              onChange={handleChange}
              className="textarea textarea-bordered"
              rows={3}
              placeholder="Translated example sentences..."
              disabled={isSaving}
            />
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text font-semibold">Notes</span>
            </label>
            <textarea
              name="user_comment"
              value={formData.user_comment ?? ""}
              onChange={handleChange}
              className="textarea textarea-bordered"
              rows={3}
              placeholder="Add personal notes or additional usage tips"
              disabled={isSaving}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-base-300">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSaving}>
              {isSaving ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                <FaSave className="mr-2" />
              )}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditWordModal;
